import React from "react";
import { LegalLayout, LegalSection } from "@/components/LegalLayout";

const SECTIONS = [
  { id: "company", title: "About the Company" },
  { id: "service", title: "Description of Service" },
  { id: "disclaimers", title: "Important Disclaimers" },
  { id: "responsibilities", title: "Your Responsibilities" },
  { id: "delivery", title: "Service Delivery" },
  { id: "payment", title: "Payment Terms" },
  { id: "ip", title: "Intellectual Property" },
  { id: "liability", title: "Limitation of Liability" },
  { id: "law", title: "Governing Law" },
  { id: "changes", title: "Changes to These Terms" },
  { id: "contact", title: "Contact" },
];

export default function Terms() {
  return (
    <LegalLayout
      testId="terms-page"
      title="Terms of Service"
      lastUpdated="February 3, 2026"
      sections={SECTIONS}
      seoTitle="Terms of Service | SchengenFile"
      seoDescription="Terms of Service for SchengenFile — Schengen visa document preparation by Trip360 Travel & Tourism Co., Riyadh, Saudi Arabia."
    >
      <p>
        Welcome to SchengenFile. These Terms of Service ("Terms") govern your access to and use of
        the SchengenFile website, dashboard, and document preparation services (collectively, the
        "Service"). By placing an order, creating an account, or otherwise using the Service, you
        agree to be bound by these Terms. If you do not agree with any part of these Terms, please
        do not use the Service.
      </p>

      <LegalSection id="company" title="1. About the Company">
        <p>
          SchengenFile (schengenfile.com) is a brand operated by <strong>Trip360 Travel & Tourism Co.</strong>,
          a company registered in Riyadh, Kingdom of Saudi Arabia. References in these Terms to
          "we", "us", "our", or "SchengenFile" mean Trip360 Travel & Tourism Co., trading as
          SchengenFile.
        </p>
      </LegalSection>

      <LegalSection id="service" title="2. Description of Service">
        <p>
          SchengenFile is a document preparation service. We help travellers assemble the supporting
          documents commonly required when submitting a short-stay Schengen visa application at a
          consulate, embassy, or accredited visa application centre. Depending on the package or
          items you order, this may include:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Verifiable flight itineraries and dummy ticket reservations with a valid PNR;</li>
          <li>Confirmed hotel bookings and accommodation vouchers;</li>
          <li>Travel medical insurance certificates that meet Schengen coverage requirements;</li>
          <li>Day-by-day travel itineraries and cover letters;</li>
          <li>Assistance completing the Schengen visa application form;</li>
          <li>Optional paid add-ons such as No-Objection Certificates, sponsor letters, and invitation letters.</li>
        </ul>
        <p>
          All documents are prepared based on the information you provide and are delivered digitally
          through your SchengenFile customer dashboard and by email.
        </p>
      </LegalSection>

      <LegalSection id="disclaimers" title="3. Important Disclaimers">
        <p className="font-semibold text-[#0f1b2d]">
          Please read this section carefully. It contains information that is critical to your
          understanding of what SchengenFile is and is not.
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>We are not a government body.</strong> SchengenFile is a private document
            preparation service. We are <em>not</em> a government agency, embassy, consulate, or
            official visa application centre, and we are not affiliated with, endorsed by, or acting
            on behalf of any Schengen member state.
          </li>
          <li>
            <strong>We do not guarantee visa approval.</strong> The decision to grant, refuse, or
            annul a visa is made solely and exclusively by the competent consular authorities of the
            destination country. Using our Service does not increase, decrease, or otherwise
            influence your likelihood of obtaining a visa, and we make no representation or warranty
            of any kind regarding the outcome of your visa application.
          </li>
          <li>
            <strong>We do not provide legal or immigration advice.</strong> Nothing on our website,
            in our documents, or in any communication from our team constitutes legal, immigration,
            tax, or financial advice. If you require such advice, please consult a qualified
            licensed professional.
          </li>
          <li>
            <strong>Document authenticity.</strong> Flight reservations, hotel confirmations, and
            insurance certificates provided by SchengenFile are real, verifiable bookings issued by
            recognised travel providers. However, they are prepared solely to support your visa
            application and, unless you explicitly instruct us otherwise, are not intended to be
            used as final purchased travel arrangements.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="responsibilities" title="4. Your Responsibilities">
        <p>By using the Service you agree that:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            You will provide accurate, complete, and up-to-date personal, passport, and travel
            information. You are solely responsible for any consequences of providing incorrect,
            incomplete, or misleading information.
          </li>
          <li>
            You have already booked, or will book, a valid visa appointment at a competent
            consulate, embassy, or visa application centre before submitting the documents we
            prepare for you.
          </li>
          <li>
            You will use the documents we prepare only for their intended purpose of supporting your
            own Schengen visa application, and you will not resell, redistribute, or misrepresent
            them.
          </li>
          <li>
            You are at least 18 years old, or you are placing the order on behalf of a minor with
            the authority of a parent or legal guardian.
          </li>
          <li>
            You will keep your account credentials confidential and notify us immediately of any
            unauthorised access.
          </li>
        </ul>
      </LegalSection>

      <LegalSection id="delivery" title="5. Service Delivery">
        <p>
          Standard orders are prepared and delivered within <strong>24 hours</strong> of successful
          payment and receipt of all required applicant information. Express orders are prepared and
          delivered within <strong>3 hours</strong> of successful payment and receipt of all
          required applicant information.
        </p>
        <p>
          Delivery timeframes begin only once (a) payment has been confirmed by our payment provider
          and (b) you have completed all necessary applicant, travel, and add-on details. Delays
          caused by incomplete information, incorrect passport data, or missing appointment details
          are not counted toward the delivery window.
        </p>
        <p>
          Documents are delivered digitally through your SchengenFile customer dashboard and are
          also emailed to the address associated with your account. It is your responsibility to
          check your dashboard, spam folder, and provided email for delivery notifications.
        </p>
      </LegalSection>

      <LegalSection id="payment" title="6. Payment Terms">
        <p>
          All prices are displayed on the website and at checkout, are quoted in US Dollars (USD),
          and are payable in full at the time of ordering. Payments are processed by our third-party
          payment provider using industry-standard encryption. SchengenFile does not store your full
          card details on its own servers.
        </p>
        <p>
          All orders are one-time purchases. Unless expressly stated, we do not operate subscriptions
          or recurring charges. Any refund entitlement is governed by our separate{" "}
          <a href="/refunds" className="text-[#2563eb] hover:underline">Refund Policy</a>, which forms
          part of these Terms.
        </p>
      </LegalSection>

      <LegalSection id="ip" title="7. Intellectual Property">
        <p>
          The SchengenFile name, logo, website design, source code, templates, mockups, sample
          documents, and all associated materials are the property of Trip360 Travel & Tourism Co.
          and are protected by copyright, trademark, and other intellectual property laws.
        </p>
        <p>
          When we deliver documents to you, we grant you a limited, non-exclusive, non-transferable
          licence to use those documents solely for the purpose of supporting your own Schengen visa
          application. You may not copy, resell, sublicense, reverse-engineer, or otherwise exploit
          our templates, dashboards, or platform.
        </p>
      </LegalSection>

      <LegalSection id="liability" title="8. Limitation of Liability">
        <p>
          To the fullest extent permitted by applicable law, SchengenFile, Trip360 Travel & Tourism
          Co., and their officers, employees, and agents shall not be liable for any indirect,
          incidental, special, consequential, or punitive damages, or for any loss of profits, data,
          bookings, business, goodwill, visa outcomes, or travel plans, whether based on contract,
          tort (including negligence), strict liability, or any other legal theory, even if we have
          been advised of the possibility of such damages.
        </p>
        <p>
          Our total aggregate liability to you for any claim arising out of or relating to the
          Service shall not exceed the amount actually paid by you to SchengenFile for the specific
          order giving rise to the claim.
        </p>
        <p>
          Nothing in these Terms excludes or limits any liability that cannot be excluded or limited
          under the mandatory laws of the Kingdom of Saudi Arabia or your country of residence.
        </p>
      </LegalSection>

      <LegalSection id="law" title="9. Governing Law and Jurisdiction">
        <p>
          These Terms are governed by, and shall be construed in accordance with, the laws of the
          Kingdom of Saudi Arabia. Any dispute, claim, or controversy arising out of or relating to
          these Terms or the Service shall be subject to the exclusive jurisdiction of the competent
          courts of Riyadh, Kingdom of Saudi Arabia, without prejudice to any mandatory consumer
          protection rights you may have under the laws of your country of residence.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="10. Changes to These Terms">
        <p>
          We may update these Terms from time to time to reflect changes to the Service, applicable
          laws, or our business practices. The "Last updated" date at the top of this page indicates
          when the Terms were most recently revised. Continued use of the Service after any update
          constitutes your acceptance of the revised Terms. Where a change is material, we will use
          reasonable efforts to notify registered users by email or through the dashboard.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="11. Contact">
        <p>
          For any questions about these Terms or the Service, you can reach us at{" "}
          <a href="mailto:support@schengenfile.com" className="text-[#2563eb] hover:underline">
            support@schengenfile.com
          </a>{" "}
          or through the messaging feature in your SchengenFile customer dashboard. Postal
          correspondence may be sent to Trip360 Travel & Tourism Co., Riyadh, Kingdom of Saudi
          Arabia.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
